/**
 * Peer marble ring — render layer. Owns the Three.js meshes for the
 * PeerRingCore's peers: tinted marble-kit variants riding the session's
 * centerline with a small seeded lateral offset + wobble (so passes read
 * as side-by-side overtakes, not superimpositions), rolling spin, and
 * scale-in/out on spawn/despawn.
 */
import * as THREE from "three";
import type { Assets } from "./assets.ts";
import { MARBLE_SCALE } from "./physics/engine.ts";
import {
  IN_TIME,
  OUT_TIME,
  type Peer,
  PeerRingCore,
  slotOffsets,
} from "./peersCore.ts";

/** Samples the session's global centerline at arc length s. */
export type FrameSampler = (
  s: number,
  pos: THREE.Vector3,
  tan: THREE.Vector3,
) => boolean;

const FALLBACK_MESH = "marble-center-high";

interface PeerVisual {
  peer: Peer;
  obj: THREE.Group;
  pivot: THREE.Group;
  radius: number;
  /** Cloned (tinted) materials to dispose with the peer. */
  materials: THREE.Material[];
  baseScale: number;
}

export class PeerRing {
  private readonly core: PeerRingCore;
  private readonly visuals = new Map<string, PeerVisual>();
  private readonly point = new THREE.Vector3();
  private readonly tangent = new THREE.Vector3();
  private readonly axis = new THREE.Vector3();
  private readonly spinQ = new THREE.Quaternion();
  private readonly up = new THREE.Vector3(0, 1, 0);

  constructor(
    seed: string,
    private readonly assets: Assets,
    private readonly parent: THREE.Object3D,
    count?: number,
  ) {
    this.core = count === undefined
      ? new PeerRingCore(seed)
      : new PeerRingCore(seed, slotOffsets(count));
  }

  get liveCount(): number {
    return this.core.liveCount;
  }

  /** Peer arc positions relative to the player (CUA/debug). */
  stats(playerS: number): { slot: number; rel: number; speed: number }[] {
    return this.core.peers.flatMap((p) =>
      p
        ? [{
          slot: p.slot,
          rel: Number((p.s - playerS).toFixed(1)),
          speed: Number(p.speed.toFixed(2)),
        }]
        : []
    );
  }

  update(
    dt: number,
    playerS: number,
    band: readonly [number, number],
    sample: FrameSampler,
  ): void {
    const events = this.core.update(
      dt,
      playerS,
      band,
      performance.now(),
      (s) => sample(s, this.point, this.tangent) ? this.tangent.y : null,
    );
    for (const peer of events.spawned) this.buildVisual(peer);
    for (const peer of events.removed) this.destroyVisual(peer);

    for (const vis of this.visuals.values()) {
      const { peer } = vis;
      if (!sample(peer.s, this.point, this.tangent)) continue;

      // Lateral offset + wobble: perpendicular to travel in the horizontal
      // plane, faded out where the tangent goes vertical (helix dives).
      const nl = Math.hypot(this.tangent.z, this.tangent.x);
      if (nl > 1e-4) {
        const off = peer.identity.lateral +
          Math.sin(
              peer.age * peer.identity.wobbleHz * Math.PI * 2 +
                peer.identity.wobblePhase,
            ) * peer.identity.wobbleAmp;
        const f = Math.min(1, nl / 0.3);
        this.point.x += (this.tangent.z / nl) * off * f;
        this.point.z += (-this.tangent.x / nl) * off * f;
      }
      vis.obj.position.copy(this.point);

      // Rolling spin, same law as the player marble.
      if (peer.phase !== "out") {
        this.axis.copy(this.up).cross(this.tangent);
        if (this.axis.lengthSq() > 1e-8) {
          this.axis.normalize();
          this.spinQ.setFromAxisAngle(
            this.axis,
            (peer.speed * dt) / Math.max(vis.radius, 1e-3),
          );
          vis.pivot.quaternion.premultiply(this.spinQ);
        }
      }

      // Spawn/despawn scale envelope (easeOutBack in, ease-in out).
      let k = 1;
      if (peer.phase === "in") {
        const t = Math.min(1, peer.phaseT / IN_TIME);
        const c = 1.70158;
        const u = t - 1;
        k = 1 + (c + 1) * u * u * u + c * u * u;
      } else if (peer.phase === "out") {
        const t = Math.min(1, peer.phaseT / OUT_TIME);
        k = Math.max(1e-3, 1 - t * t);
      }
      vis.obj.scale.setScalar(k * vis.baseScale);
    }
  }

  dispose(): void {
    for (const peer of this.core.clear()) this.destroyVisual(peer);
  }

  private buildVisual(peer: Peer): void {
    const obj = new THREE.Group();
    obj.name = `peer:${peer.label}`;
    const pivot = new THREE.Group();
    obj.add(pivot);
    const mesh = this.assets.getPiece(peer.identity.meshId) ??
      this.assets.getPiece(FALLBACK_MESH);
    let radius = 0.1;
    const materials: THREE.Material[] = [];
    if (mesh) {
      mesh.scale.setScalar(MARBLE_SCALE * peer.identity.scale);
      if (peer.identity.tint) {
        const [r, g, b] = peer.identity.tint;
        mesh.traverse((child: THREE.Object3D) => {
          const m = child as THREE.Mesh;
          if (!m.isMesh) return;
          const tintOne = (mat: THREE.Material): THREE.Material => {
            const clone = (mat as THREE.MeshStandardMaterial).clone();
            clone.color.setRGB(r / 255, g / 255, b / 255);
            materials.push(clone);
            return clone;
          };
          m.material = Array.isArray(m.material)
            ? m.material.map(tintOne)
            : tintOne(m.material);
        });
      }
      const box = new THREE.Box3().setFromObject(mesh);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      radius = Math.max(size.x, size.y, size.z) / 2 || 0.1;
      pivot.position.copy(center);
      mesh.position.sub(center);
      pivot.add(mesh);
    }
    obj.scale.setScalar(1e-3); // grows in via the scale envelope
    this.parent.add(obj);
    this.visuals.set(peer.label, {
      peer,
      obj,
      pivot,
      radius,
      materials,
      baseScale: 1,
    });
  }

  private destroyVisual(peer: Peer): void {
    const vis = this.visuals.get(peer.label);
    if (!vis) return;
    this.visuals.delete(peer.label);
    this.parent.remove(vis.obj);
    vis.obj.clear();
    for (const m of vis.materials) m.dispose();
  }
}
